'use client';

import type { MouseEvent } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslations } from 'next-intl';

import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import uniqBy from 'lodash/uniqBy';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

import { ButtonIcon } from '../buttonIcon/ButtonIcon';
import { calcFileSizeFromBytes } from '../../utils/file/size/fileSize.util';

import {
  ChipStyled,
  CloudUploadStyled,
  DropzoneLabelAreaStyled,
  DropzoneStyled,
  LabelStyled,
  UploadFilesStyled,
} from './uploadFiles.styled';

import type { TNameType } from '../../types/components/components.type';
import type { IUploadFilesProps } from './uploadFiles.type';

export const UploadFiles = <TName extends TNameType>({
  name,
  className,
  label,
  handleChange,
  disabled = false,
  errorMessage = '',
  helperText,
  minHeight,
  value,
}: IUploadFilesProps<TName>) => {
  const t = useTranslations();

  const handleLocalChange = (files: File[]) => {
    if (!disabled) {
      const newState = files.length > 0 ? uniqBy([...value, ...files], 'name') : [];

      handleChange({ key: name, value: newState });
    }
  };

  const handleRemoveFile = (fileName: string) => {
    if (!disabled) {
      const newState = value.filter(({ name: currentFileName }) => currentFileName !== fileName);

      handleChange({ key: name, value: newState });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleLocalChange,
    disabled,
  });

  const isError = !!errorMessage;
  const fileUploaderId = `${name}-file-uploader`;

  return (
    <UploadFilesStyled id={name} className={className}>
      <DropzoneStyled {...getRootProps()} role="button" error={isError} minHeight={minHeight}>
        <input {...getInputProps()} type="file" name={name} id={fileUploaderId} />

        <Stack spacing={2}>
          <DropzoneLabelAreaStyled>
            <LabelStyled disabled={disabled} htmlFor={fileUploaderId}>
              {label}
            </LabelStyled>
            <CloudUploadStyled disabled={disabled} size={value.length > 0 ? 'small' : 'large'} />
            {helperText && (
              <LabelStyled disabled={disabled} htmlFor={fileUploaderId}>
                {helperText}
              </LabelStyled>
            )}
          </DropzoneLabelAreaStyled>

          <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
            {value.map(({ name: fileName, size }) => (
              <ChipStyled
                key={fileName}
                disabled={disabled}
                onDelete={(e: MouseEvent<SVGAElement>) => {
                  e.stopPropagation();

                  handleRemoveFile(fileName);
                }}
                deleteIcon={<CancelRoundedIcon aria-label={`${t('labels.delete')} ${fileName}`} />}
                label={`${fileName} (${calcFileSizeFromBytes(size)})`}
              />
            ))}

            {value.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">
                  {t('labels.files', { count: value.length })}
                </Typography>
                <ButtonIcon
                  size="small"
                  handleClick={(e) => {
                    e.stopPropagation();

                    handleLocalChange([]);
                  }}
                  aria-label={t('labels.delete')}
                  aria-haspopup={false}
                >
                  <CancelRoundedIcon color="disabled" />
                </ButtonIcon>
              </Box>
            )}
          </Stack>
        </Stack>
      </DropzoneStyled>

      {isError && (
        <FormHelperText error sx={{ marginLeft: '14px' }}>
          {errorMessage}
        </FormHelperText>
      )}
    </UploadFilesStyled>
  );
};
